import {
  type AwgSize,
  type CalcResult,
  type ConductorMaterial,
  type TempRating,
  err,
  isAwgSize,
  isFiniteNumber,
  isPositiveFinite,
  ok,
} from "../types.ts";
import {
  MAX_AMBIENT_C,
  TABLE_310_16,
  bundleAdjustmentFactor,
  tempCorrectionFactor,
} from "../tables/ampacity.ts";

export type AmpacityInput = {
  awg: AwgSize;
  material: ConductorMaterial;
  tempRating: TempRating; // insulation temperature column (60C / 75C / 90C)
  ambientC: number; // ambient temperature in °C
  currentCarryingConductors: number; // total in the raceway/cable, for bundling
};

export type AmpacityResult = {
  baseAmpacityA: number; // Table 310.16 value before adjustments
  tempCorrectionFactor: number; // 310.15(B)(1)
  adjustmentFactor: number; // 310.15(C)(1) bundling
  deratedAmpacityA: number; // final usable ampacity
};

/**
 * Conductor ampacity per NEC 2023:
 *   base   = Table 310.16 [material][awg][tempRating]
 *   ×      Table 310.15(B)(1) ambient temperature correction
 *   ×      Table 310.15(C)(1) adjustment for >3 current-carrying conductors
 *
 * Returns the derated ampacity plus each factor so the UI can show the work.
 */
export function ampacity(input: AmpacityInput): CalcResult<AmpacityResult> {
  const { awg, material, tempRating, ambientC, currentCarryingConductors } = input;

  if (!isAwgSize(awg)) {
    return err(`Unsupported conductor size "${String(awg)}".`);
  }
  if (material !== "copper" && material !== "aluminum") {
    return err(`Material must be "copper" or "aluminum".`);
  }
  if (tempRating !== "60C" && tempRating !== "75C" && tempRating !== "90C") {
    return err(`Temperature rating must be 60C, 75C, or 90C.`);
  }
  if (!isFiniteNumber(ambientC)) {
    return err("Ambient temperature (°C) must be a number.");
  }
  if (!isPositiveFinite(currentCarryingConductors) || !Number.isInteger(currentCarryingConductors)) {
    return err("Number of current-carrying conductors must be a whole number ≥ 1.");
  }

  const base = TABLE_310_16[material][awg][tempRating];
  if (base === null) {
    return err(`${awg} AWG is not available in ${material}.`);
  }

  const correction = tempCorrectionFactor(ambientC, tempRating);
  if (correction === null) {
    return err(
      `Ambient ${ambientC}°C exceeds the ${tempRating} column limit (${MAX_AMBIENT_C[tempRating]}°C). Use a higher-rated insulation.`,
    );
  }

  const adjustment = bundleAdjustmentFactor(currentCarryingConductors);
  const deratedAmpacityA = base * correction * adjustment;

  return ok({
    baseAmpacityA: base,
    tempCorrectionFactor: correction,
    adjustmentFactor: adjustment,
    deratedAmpacityA,
  });
}
